import { Search as SearchIcon } from '@mui/icons-material';
import {
  Autocomplete,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { useQueryClient } from '@tanstack/react-query';
import { useMapsLibrary } from '@vis.gl/react-google-maps';
import { AlertTriangle, Check, Clock3 } from 'lucide-react';
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router';

import { AuthBrandHeader } from '@/components/common/AuthBrandHeader';
import { FixedCard } from '@/components/common/FixedCard';
import { FullScreenContainer } from '@/components/common/FullScreenContainer';
import { PropertyAddressMapCard } from '@/components/common/PropertyAddressMapCard';
import { CLIENT_GROUPS_QUERY_KEY } from '@/hooks/useClientGroups';
import { selfClaimProperty } from '@/services/api/properties/api';
import {
  fetchAddressSuggestions,
  formatStreetAddress,
  normalizeUnit,
  parseGoogleAddressComponents,
} from '@/types/maps';

interface ParsedAddress {
  address: string;
  city: string;
  state: string;
  zipCode: string;
  fullAddress: string;
  latitude?: number;
  longitude?: number;
}

const font = "'Manrope', sans-serif";

const ClaimHomeAddress = () => {
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const placesLib = useMapsLibrary('places');
  const mapsLib = useMapsLibrary('maps');
  const [geocoder, setGeocoder] = useState<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (mapsLib) setGeocoder(new google.maps.Geocoder());
  }, [mapsLib]);

  const [addressQuery, setAddressQuery] = useState('');
  const [addressOptions, setAddressOptions] = useState<google.maps.places.AutocompletePrediction[]>(
    [],
  );
  const [selectedAddress, setSelectedAddress] =
    useState<google.maps.places.AutocompletePrediction | null>(null);
  const [parsedAddress, setParsedAddress] = useState<ParsedAddress | null>(null);
  const [unit, setUnit] = useState('');
  const [isSearching, setIsSearching] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pendingReviewAddress, setPendingReviewAddress] = useState<string | null>(null);

  useEffect(() => {
    if (!addressQuery || addressQuery.length < 3 || selectedAddress) {
      if (!addressQuery || addressQuery.length < 3) setAddressOptions([]);
      return;
    }
    if (!placesLib) return;

    const timeoutId = setTimeout(async () => {
      setIsSearching(true);
      try {
        const suggestions = await fetchAddressSuggestions(addressQuery, placesLib);
        setAddressOptions(suggestions);
      } catch {
        setAddressOptions([]);
      } finally {
        setIsSearching(false);
      }
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [addressQuery, selectedAddress, placesLib]);

  useEffect(() => {
    if (!selectedAddress) {
      setParsedAddress(null);
      return;
    }
    if (!geocoder) return;

    setError(null);
    let cancelled = false;

    geocoder.geocode({ placeId: selectedAddress.place_id }, (results, status) => {
      if (cancelled) return;
      if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
        setParsedAddress(null);
        return;
      }
      const result = results[0];
      const components = parseGoogleAddressComponents(result.address_components);
      setUnit(components.unit ?? '');
      setParsedAddress({
        address: [components.streetNumber, components.street].filter(Boolean).join(' '),
        city: components.city ?? '',
        state: components.state ?? '',
        zipCode: components.zip ?? '',
        fullAddress: result.formatted_address ?? selectedAddress.description,
        latitude: result.geometry.location.lat(),
        longitude: result.geometry.location.lng(),
      });
    });

    return () => {
      cancelled = true;
    };
  }, [selectedAddress, geocoder]);

  const canSubmit = parsedAddress && !isSubmitting;

  const handleSubmit = async () => {
    if (!parsedAddress) return;

    setError(null);
    setIsSubmitting(true);

    try {
      const streetAddress = formatStreetAddress(parsedAddress.address, unit);
      const fullAddress = `${streetAddress}, ${parsedAddress.city}, ${parsedAddress.state} ${parsedAddress.zipCode}`;
      const result = await selfClaimProperty({
        address: parsedAddress.address,
        unit: normalizeUnit(unit) || undefined,
        city: parsedAddress.city,
        state: parsedAddress.state,
        zipCode: parsedAddress.zipCode,
        fullAddress,
        latitude: parsedAddress.latitude,
        longitude: parsedAddress.longitude,
      });

      await queryClient.invalidateQueries({ queryKey: ['properties'] });
      await queryClient.invalidateQueries({ queryKey: CLIENT_GROUPS_QUERY_KEY });

      if (result.status === 'pending_review') {
        setPendingReviewAddress(fullAddress);
        setIsSubmitting(false);
        return;
      }

      navigate('/dashboard/home-hub', { replace: true });
    } catch (submitError: any) {
      setError(
        submitError?.response?.data?.message ?? 'Failed to claim this home. Please try again.',
      );
      setIsSubmitting(false);
    }
  };

  if (pendingReviewAddress) {
    const locality = [parsedAddress?.city, parsedAddress?.state].filter(Boolean).join(', ');
    return (
      <FullScreenContainer disablePadding>
        <FixedCard>
          <CardContent
            sx={{ gap: 5, alignItems: 'center', justifyContent: 'space-between', pt: 14 }}>
            <AuthBrandHeader />
            <Stack
              spacing="18px"
              sx={{ width: '100%', flexGrow: 1, justifyContent: 'center' }}
              textAlign="center"
              data-testid="claim-home-pending-review">
              <Box>
                <Typography variant="h6" fontWeight={600}>
                  Request sent
                </Typography>
                <Typography
                  color="text.secondary"
                  sx={{ mt: '6px', fontSize: 13, lineHeight: 1.5 }}>
                  This home is already in Hauser. We've sent your claim request for review.
                </Typography>
              </Box>
              <Box sx={{ width: '100%', mt: '20px !important' }}>
                <PropertyAddressMapCard
                  compact
                  address={
                    parsedAddress
                      ? formatStreetAddress(parsedAddress.address, unit)
                      : pendingReviewAddress
                  }
                  locality={`${locality} ${parsedAddress?.zipCode ?? ''}`.trim()}
                />
              </Box>
              <Box
                sx={{
                  position: 'relative',
                  display: 'grid',
                  gridTemplateColumns: 'repeat(3, 1fr)',
                  mt: '20px',
                  transform: 'translateY(14px)',
                }}>
                {[
                  ['16.66%', '50%'],
                  ['50%', '16.66%'],
                ].map(([left, right], index) => (
                  <Box
                    key={left}
                    sx={{
                      position: 'absolute',
                      top: 11,
                      left,
                      right,
                      borderTop: `1px dashed rgba(187,183,175,${index === 0 ? '0.45' : '0.24'})`,
                    }}
                  />
                ))}
                {['Submitted', 'In review', 'Access'].map((label, index) => (
                  <Box key={label} sx={{ position: 'relative', textAlign: 'center' }}>
                    <Box
                      sx={{
                        position: 'relative',
                        zIndex: 1,
                        width: index === 1 ? 27 : 23,
                        height: index === 1 ? 27 : 23,
                        mx: 'auto',
                        display: 'grid',
                        placeItems: 'center',
                        borderRadius: '50%',
                        bgcolor: index < 2 ? 'var(--tx-fill-2)' : 'var(--tx-glass-panel)',
                        border: index < 2 ? 'none' : '1px solid var(--tx-line)',
                        color: index < 2 ? 'var(--tx-text)' : 'text.disabled',
                        boxShadow: index === 1 ? '0 0 0 5px rgba(187,183,175,0.1)' : 'none',
                        ...(index === 1 && {
                          '&::after': {
                            content: '""',
                            position: 'absolute',
                            inset: -5,
                            borderRadius: '50%',
                            border: '1px solid var(--tx-line)',
                            animation: 'reviewPulse 2s ease-out infinite',
                          },
                          '@keyframes reviewPulse': {
                            '0%': { transform: 'scale(0.85)', opacity: 0.7 },
                            '70%, 100%': { transform: 'scale(1.45)', opacity: 0 },
                          },
                          '@media (prefers-reduced-motion: reduce)': {
                            '&::after': { animation: 'none' },
                          },
                        }),
                      }}>
                      {index === 0 ? <Check size={12} strokeWidth={2.5} /> : <Clock3 size={11} />}
                    </Box>
                    <Typography
                      sx={{
                        fontFamily: font,
                        fontSize: 11,
                        fontWeight: index < 2 ? 650 : 500,
                        color: index < 2 ? 'text.secondary' : 'text.disabled',
                        mt: '6px',
                      }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Stack>
          </CardContent>
        </FixedCard>
      </FullScreenContainer>
    );
  }

  return (
    <FullScreenContainer disablePadding>
      <FixedCard>
        <CardContent sx={{ gap: 5, alignItems: 'center', justifyContent: 'space-between', pt: 14 }}>
          <AuthBrandHeader />

          <Box>
            <Typography variant="h6" fontWeight={600} textAlign="center" sx={{ mb: 1 }}>
              What's your home address?
            </Typography>
            <Typography variant="body2" color="text.secondary" textAlign="center">
              We'll set up your Home HQ for this property.
            </Typography>
          </Box>

          <Autocomplete
            fullWidth
            sx={{ width: '100%' }}
            freeSolo
            options={addressOptions}
            getOptionLabel={(option) => (typeof option === 'string' ? option : option.description)}
            value={selectedAddress}
            onChange={(_, newValue) => {
              if (typeof newValue !== 'string') setSelectedAddress(newValue);
            }}
            inputValue={addressQuery}
            onInputChange={(_, newInputValue) => {
              setAddressQuery(newInputValue);
              if (selectedAddress) setSelectedAddress(null);
            }}
            loading={isSearching}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Start typing your address..."
                data-testid="claim-home-address-input"
                autoFocus
                slotProps={{
                  input: {
                    ...params.InputProps,
                    startAdornment: (
                      <SearchIcon sx={{ fontSize: 18, color: 'text.secondary', mr: 0.5 }} />
                    ),
                    endAdornment: (
                      <>
                        {isSearching && <CircularProgress size={16} />}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  },
                }}
              />
            )}
            renderOption={(props, option) => (
              <li {...props} key={option.place_id} style={{ fontFamily: font, fontSize: 13 }}>
                {option.description}
              </li>
            )}
          />

          {parsedAddress && (
            <TextField
              fullWidth
              value={unit}
              onChange={(event) => setUnit(event.target.value)}
              placeholder="Unit / Apt (optional)"
              inputProps={{ 'aria-label': 'Unit or apartment number', maxLength: 30 }}
            />
          )}

          {parsedAddress && (
            <Box data-testid="claim-home-address-preview" sx={{ width: '100%' }}>
              <PropertyAddressMapCard
                address={formatStreetAddress(parsedAddress.address, unit)}
                locality={`${parsedAddress.city}, ${parsedAddress.state} ${parsedAddress.zipCode}`}
              />
            </Box>
          )}

          {error && (
            <Box
              data-testid="claim-home-error-message"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: '8px',
                p: '9px 11px',
                borderRadius: '8px',
                border: 0,
                bgcolor: 'rgba(219,82,130,0.05)',
                color: '#d9799b',
              }}>
              <AlertTriangle size={14} style={{ flexShrink: 0 }} />
              <Typography sx={{ fontSize: 12 }}>{error}</Typography>
            </Box>
          )}

          <Button
            fullWidth
            onClick={canSubmit ? handleSubmit : undefined}
            disabled={!canSubmit}
            data-testid="claim-home-submit-btn"
            sx={{ height: 40 }}>
            {isSubmitting ? <CircularProgress size={18} sx={{ color: '#262626' }} /> : 'Continue'}
          </Button>
        </CardContent>
      </FixedCard>
    </FullScreenContainer>
  );
};

export default ClaimHomeAddress;
